import { useQuery } from '@tanstack/react-query';

import { postData } from 'src/app/queries';

import { workflowCampaignsQueryKey } from './get-workflow-campaigns';

import type { UtmParameter } from './get-campaigns';
import type {
  MarketingEmailData,
  MarketingPointBoostData,
  MarketingPushData,
  MarketingSMSData,
} from './upsert-campaign';

export type BaseWorkflowNode = {
  Id: number;
};

export type BaseActionWorkflowNode = BaseWorkflowNode & {
  Type: 'action';
};

export type SendEmailActionWorkflowNode = BaseActionWorkflowNode & {
  ActionType: 'sendEmail';
  EmailConfig: MarketingEmailData;
};

export type SendSMSActionWorkflowNode = BaseActionWorkflowNode & {
  ActionType: 'sendSMS';
  SMSConfig: MarketingSMSData;
};

export type SendPushActionWorkflowNode = BaseActionWorkflowNode & {
  ActionType: 'sendPushNotification';
  PushConfig: MarketingPushData;
};

export type PointBoostWorkflowNode = BaseActionWorkflowNode & {
  ActionType: 'grantPoints';
  PointBoostConfig: MarketingPointBoostData;
};

export type ActionWorkflowNode =
  | PointBoostWorkflowNode
  | SendEmailActionWorkflowNode
  | SendPushActionWorkflowNode
  | SendSMSActionWorkflowNode;

export type TimeDelayWorkflowNode = BaseWorkflowNode & {
  Duration: string;
  TimeOfDay: string | null;
  TimeUnitId: number;
  Type: 'timeDelay'; // TimeSpan represented as string in JSON
};

export type BaseConditionalWorkflowNode = BaseWorkflowNode & {
  Type: 'conditional'; // TimeSpan represented as string in JSON
};

export type EmailClickedConditionalWorkflowNode = BaseConditionalWorkflowNode & {
  ConditionalReferenceNodeId: number | null;
  ConditionalType: 'clickedEmail';
  Duration: string;
  TimeUnitId: number;
};

export type EmailOpenedConditionalWorkflowNode = BaseConditionalWorkflowNode & {
  ConditionalReferenceNodeId: number | null;
  ConditionalType: 'openedEmail';
  Duration: string;
  TimeUnitId: number;
};

export type SMSOpenedConditionalWorkflowNode = BaseConditionalWorkflowNode & {
  ConditionalReferenceNodeId: number | null;
  ConditionalType: 'openedSMS';
  Duration: string;
  TimeUnitId: number;
};

export type PushOpenedConditionalWorkflowNode = BaseConditionalWorkflowNode & {
  ConditionalReferenceNodeId: number | null;
  ConditionalType: 'openedPush';
  Duration: string;
  TimeUnitId: number;
};

export type SegmentConditionalWorkflowNode = BaseConditionalWorkflowNode & {
  ConditionalType: 'isInSegment';
  SegmentIds: number[]; // Required for segment conditionals
};

export type PurchaseConditionalWorkflowNode = BaseConditionalWorkflowNode & {
  ConditionalType: 'madeAPurchase';
  Duration: string;
  TimeUnitId: number;
};

export type ConditionalWorkflowNode =
  | EmailClickedConditionalWorkflowNode
  | EmailOpenedConditionalWorkflowNode
  | PurchaseConditionalWorkflowNode
  | PushOpenedConditionalWorkflowNode
  | SegmentConditionalWorkflowNode
  | SMSOpenedConditionalWorkflowNode;

export type WorkflowNode = ActionWorkflowNode | ConditionalWorkflowNode | TimeDelayWorkflowNode;

export type WorkflowEdge = {
  EdgeType: 'next' | 'no' | 'yes';
  Source: number;
  Target: number;
};

export type WorkflowMarketingCampaign = {
  CampaignName: string;
  CampaignStatusId: number;
  CustomUtmParameters: UtmParameter[];
  DelayInMinutes: number | null;
  DelayUnitId: number | null;
  Edges: WorkflowEdge[];
  EventTypeId: number;
  LastModifiedDateUTC: string | null;
  LastRuntimeDateUtc: string | null;
  LspId: number;
  MarketingCampaignId: number | null;
  Nodes: WorkflowNode[];
  RecipientSegmentIds: number[];
  ScheduleSendForUtc: string | null;
  SendToAllCustomers: boolean;
  TagIds: number[];
  TimeOfDayToSend: string | null;
};

type GetWorkflowCampaignPayload = {
  MarketingCampaignId: number | null;
};

export function useGetWorkflowCampaignQuery({ marketingCampaignId }: { marketingCampaignId: number | null }) {
  return useQuery({
    queryKey: [...workflowCampaignsQueryKey, marketingCampaignId],
    enabled: !!marketingCampaignId,
    staleTime: 0,
    queryFn: async () =>
      postData<GetWorkflowCampaignPayload, WorkflowMarketingCampaign>({
        endpoint: 'api/marketing-campaign/get-workflow-campaign',
        payload: { MarketingCampaignId: marketingCampaignId },
      }),
  });
}
